import { BellRing, Mail, MessageSquare, Smartphone, UserRound, type LucideIcon } from 'lucide-react'
import { EndpointHint, FormError, LoadFailure, Skeleton, TIER_LABELS } from '../../../core/index.js'
import { schedulingRulesOf, useFacilityConfig } from '../../../facilities/index.js'
import { useBoardActions, useBridgeCallouts, usePostingAudience } from '../../hooks.js'
import { calloutForPosting, calloutLine } from './postingCallout.js'
import { PostingActions } from './PostingActions.js'
import type { Posting, NoticeChannel, NotToldReason, PostingAudience, PostingAudienceMember, PostingHistoryEntry, PostingMoveAhead } from '../../types.js'

const EMPLOYMENT_LABEL: Record<PostingAudienceMember['employmentType'], string> = { full_time: 'full time', part_time: 'part time', prn: 'PRN', agency: 'agency' }
const TIER_DOT: Record<PostingAudienceMember['tier'], string> = { gold: 'bg-gold', silver: 'bg-silver', bronze: 'bg-bronze', probationary: 'bg-prob' }

const NOT_TOLD_LABEL: Record<NotToldReason, string> = {
  working_that_day: 'Working that day',
  left: 'Left before the day',
  other_unit: 'Not on this unit',
  credentials: 'Missing a credential',
  day_after_evening: 'Would be a day after an evening',
  max_consecutive_days: 'Too many days in a row',
  not_yet_offered: 'Not offered yet',
  free_since_blast: 'Free only since everyone was told',
}

const CHANNEL: Record<string, { icon: LucideIcon; name: string }> = {
  sms: { icon: MessageSquare, name: 'Text' },
  'web-push': { icon: BellRing, name: 'Push' },
  'ses-email': { icon: Mail, name: 'Email' },
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

/** One channel in words and colour: green delivered, gold not sent yet and why, coral failed and why. */
function channelText(channel: NoticeChannel): { text: string; tone: string } {
  const name = CHANNEL[channel.channel]?.name ?? channel.channel
  switch (channel.status) {
    case 'sent':
      return { text: name === 'Text' ? 'Text delivered' : name, tone: 'text-green' }
    case 'held':
      return { text: `${name} held${channel.heldUntil ? ` until ${time(channel.heldUntil)}` : ''}`, tone: 'text-[#8a6a1a]' }
    case 'skipped':
      return { text: `No ${name.toLowerCase()}${channel.reason ? `: ${channel.reason}` : ''}`, tone: 'text-[#8a6a1a]' }
    case 'opted_out':
      return { text: `${name}: replied STOP`, tone: 'text-coral' }
    case 'address_gone':
      return { text: `${name} failed: the address no longer works`, tone: 'text-coral' }
    default:
      return { text: `${name} failed${channel.reason ? `: ${channel.reason}` : ''}`, tone: 'text-coral' }
  }
}

function ReachedBy({ member }: { member: PostingAudienceMember }) {
  const channels = member.reach?.channels ?? []
  return (
    <span className="flex flex-wrap gap-x-2.5 gap-y-0.5">
      <span className="inline-flex items-center gap-1 text-green">
        <Smartphone className="h-3 w-3" aria-hidden="true" />
        {channels.length === 0 ? 'App only: no alerts switched on' : 'App'}
      </span>
      {channels.map((channel) => {
        const Icon = CHANNEL[channel.channel]?.icon ?? MessageSquare
        const { text, tone } = channelText(channel)
        return (
          <span key={channel.channel} className={`inline-flex items-center gap-1 ${tone}`}>
            <Icon className="h-3 w-3" aria-hidden="true" />
            {text}
          </span>
        )
      })}
    </span>
  )
}

function answerText(member: PostingAudienceMember): { text: string; tone: string } {
  if (member.answer === 'accepted') {
    return { text: `Took it · ${time(member.answeredAt ?? member.toldAt)}`, tone: 'text-green font-bold' }
  }
  if (member.answer === 'declined') {
    return { text: `Can't make it${member.answeredAt ? ` · ${time(member.answeredAt)}` : ''}`, tone: 'text-coral' }
  }
  return { text: member.expiresAt ? `No answer · offer ends ${time(member.expiresAt)}` : 'No answer', tone: 'text-ink-mid' }
}

/** The summary across everyone told: how many, how many each channel reached, seen, said no, no answer. */
function Tiles({ audience }: { audience: PostingAudience }) {
  const told = audience.told
  const sentOn = (key: string) => told.filter((member) => member.reach?.channels.some((channel) => channel.channel === key && channel.status === 'sent')).length
  const outside = ['sms', 'web-push', 'ses-email'].map((key) => [CHANNEL[key]?.name.toLowerCase() ?? key, sentOn(key)] as const).filter(([, count]) => count > 0)
  const tiles = [
    { label: 'Told', value: told.length, note: audience.blastAt ? `at once at ${time(audience.blastAt)}` : 'in turn' },
    { label: 'Reached outside the app', value: told.filter((member) => member.reach?.channels.some((channel) => channel.status === 'sent')).length, note: outside.length > 0 ? outside.map(([name, count]) => `${name} ${count}`).join(' · ') : 'app only' },
    { label: 'Seen', value: told.filter((member) => member.reach?.read).length, note: 'opened the message' },
    { label: "Can't make it", value: told.filter((member) => member.answer === 'declined').length, note: 'said no' },
    { label: 'No answer', value: told.filter((member) => member.answer === null).length, note: 'yet' },
  ]
  return (
    <div className="flex flex-wrap border-b border-marine/10">
      {tiles.map((tile) => (
        <div key={tile.label} className="min-w-[6rem] flex-1 border-r border-marine/10 px-3 py-2 last:border-r-0">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{tile.label}</div>
          <div className="text-lg font-black leading-tight text-ink">{tile.value}</div>
          <div className="text-[10px] text-ink-mid">{tile.note}</div>
        </div>
      ))}
    </div>
  )
}


const HISTORY_TEXT: Record<PostingHistoryEntry['kind'], (entry: PostingHistoryEntry, nameOf: (workerId: string) => string) => string> = {
  born: (entry) => `Born: ${BORN_OF[entry.bornOf ?? ''] ?? entry.source ?? 'open'}${entry.vacatedFrom ? ` (${entry.vacatedFrom})` : ''}`,
  posted: (entry) => `Posted · in ${entry.told ?? 0} ${entry.told === 1 ? 'worker’s' : 'workers’'} daily digest`,
  nudged: (entry) => `Nudged · in ${entry.told ?? 0} ${entry.told === 1 ? 'worker’s' : 'workers’'} digest again`,
  offered: (entry, nameOf) => `Offered to ${nameOf(entry.workerId ?? '')}${entry.until ? ` until ${time(entry.until)}` : ''}`,
  offer_declined: (entry, nameOf) => `${nameOf(entry.workerId ?? '')} declined the offer`,
  offer_expired: (entry, nameOf) => `${nameOf(entry.workerId ?? '')}’s offer ran out`,
  blasted: (entry) => `Urgent: ${entry.told ?? 0} told at once`,
  repeated: (entry) => `Told again: ${entry.told ?? 0}`,
  declined: (entry, nameOf) => `${nameOf(entry.workerId ?? '')} can’t make it`,
  callout_opened: () => 'Clinical Bridge callout opened',
  locked: () => 'Locked: under Needs you',
  filled: (entry, nameOf) => `Filled by ${nameOf(entry.workerId ?? '')}${entry.mode ? ` · ${PICKUP[entry.mode] ?? entry.mode}` : ''}`,
  ran_short: (entry) => `Ran short by decision${entry.reasonCode ? ` · ${entry.reasonCode}` : ''}`,
  withdrawn: (entry) => `Withdrawn · ${WITHDRAWN[entry.reason ?? ''] ?? 'no longer needed'}`,
  ended_unfilled: () => 'Started with nobody in it: ended unfilled',
}
const BORN_OF: Record<string, string> = { roster_short: 'the roster could not cover it', change_granted: 'a granted change freed it', call_out: 'a call-out', call_out_reported: 'a call-out the scheduling system reported', call_out_by_worker: 'the worker called out', census_rise: 'the census rose', bridge_unanswered: 'a Bridge callout nobody answered', left: 'the worker left', recall_expired: 'a recall ran out', demo: 'a demo seat' }
const PICKUP: Record<string, string> = { first_come: 'first come', offer: 'from an offer', override: 'by override', bridge: 'through the Bridge callout' }
const WITHDRAWN: Record<string, string> = { census_fall: 'the census fell', unpublished: 'the shift was unpublished', demo: 'the demo seat was taken down' }
const AHEAD_TEXT: Record<PostingMoveAhead['stage'], string> = { posted: 'Posted: put in the daily digest of everyone eligible, first to accept gets it', urgent: 'Urgent: everyone eligible told at once, whoever accepts first gets it', bridge_callout: 'The engine opens a Clinical Bridge callout for it, Bridge being on', critical: 'The lock: under Needs you, the engine’s automatic moves spent', started: 'The shift starts; still open, it closes as ended unfilled and is logged' }

/** The seat's life as a timeline (open shift lifecycle plan section 8): what happened, then what the engine does next. */
function SeatTimeline({ audience }: { audience: PostingAudience }) {
  const nameOf = (workerId: string) => audience.told.find((member) => member.workerId === workerId)?.name ?? workerId
  if (audience.history.length === 0 && audience.ahead.length === 0) {
    return null
  }
  return (
    <div className="border-b border-marine/10 px-3 py-2">
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">The seat’s life</div>
      <ol className="mt-1 space-y-0.5 text-[11px] leading-relaxed">
        {audience.history.map((entry, index) => (
          <li key={`${entry.kind}-${entry.at}-${index}`} className="flex gap-2 text-ink">
            <span className="w-24 shrink-0 tabular-nums text-ink-mid">{dayTime(entry.at)}</span>
            <span className="min-w-0">
              <span className="font-bold">{HISTORY_TEXT[entry.kind](entry, nameOf)}</span>
              {/* The engine's account, in its own words from the API (seat life reasons plan D1). */}
              {entry.why && <span className="block text-[11px] text-ink-mid">{entry.why}</span>}
            </span>
          </li>
        ))}
        {audience.ahead.map((move) => (
          <li key={`${move.stage}-${move.at}`} className="flex gap-2 text-ink-mid">
            <span className="w-24 shrink-0 tabular-nums">{dayTime(move.at)}</span>
            <span>Next: {AHEAD_TEXT[move.stage]}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

const dayTime = (iso: string): string => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

/**
 * Who was told about one open seat (who was told design, 26 October 2026): everyone the engine told, how the notice
 * reached each of them, whether they have seen it and what they answered, then those of the role it left out and why.
 * Assign is the supervisor's override, as in Needs you; `PostingActions` under the list assigns someone not told, opens a
 * Bridge callout or runs the seat short.
 */
export function PostingAudiencePanel({ facilityId, period, postingId, canOpenCallout = false, vacatedFrom = null, calloutId = null }: { facilityId: string; period: string; postingId: string; canOpenCallout?: boolean; vacatedFrom?: Posting['vacatedFrom']; calloutId?: string | null }) {
  const query = usePostingAudience(facilityId, period, postingId)
  const board = useBoardActions(facilityId, period)
  const callouts = useBridgeCallouts(facilityId)
  const config = useFacilityConfig(facilityId)
  if (query.isPending) {
    return <Skeleton lines={4} className="p-3" />
  }
  if (query.isError) {
    return <LoadFailure size="xs" message="Who was told could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
  }
  const audience = query.data
  // The supervisor's override is the facility's order's, inside the lock only (open shift lifecycle plan D5): first come,
  // nobody assigns a seat by hand, and the API answers 409 to both - so the buttons are not offered.
  const facilityOrder = config.data ? schedulingRulesOf(config.data).coverage.openBoardMode === 'facility_order' : false
  const assignable = audience.status === 'open' && audience.phase === 'locked' && facilityOrder
  const assigning = (workerId: string) => board.assign.isPending && board.assign.variables?.workerId === workerId
  const callout = calloutForPosting({ postingId, calloutId }, callouts.data)
  return (
    <div className="overflow-hidden rounded border border-marine/15 bg-white">
      {audience.filledBy && (
        <div className="border-b border-marine/10 bg-green-lt px-3 py-2 text-[11px] text-ink">
          <strong>Taken by {audience.filledBy.name}</strong> at {time(audience.filledBy.at)}
          {audience.filledBy.mode === 'override' ? ', assigned by a supervisor' : audience.filledBy.mode === 'bridge' ? ', who took its Clinical Bridge callout' : audience.blastAt ? `, ${Math.max(0, Math.round((Date.parse(audience.filledBy.at) - Date.parse(audience.blastAt)) / 60000))} min after everyone was told` : ''}.
        </div>
      )}
      <SeatTimeline audience={audience} />
      {audience.told.length > 0 ? (
        <>
          <Tiles audience={audience} />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-[11px]">
              <thead>
                <tr className="bg-silver-lt text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
                  <th className="px-2 py-1.5">Told</th>
                  <th className="px-2 py-1.5">Tier · score</th>
                  <th className="px-2 py-1.5">Reached by</th>
                  <th className="px-2 py-1.5">Seen</th>
                  <th className="px-2 py-1.5">Answer</th>
                  <th className="px-2 py-1.5">
                    <span className="sr-only">Assign</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {audience.told.map((member) => {
                  const answer = answerText(member)
                  return (
                    <tr key={`${member.workerId}-${member.via}`} className="border-b border-marine/10 align-top last:border-b-0">
                      <td className="px-2 py-1.5">
                        <span className="font-bold text-ink">{member.name}</span>
                        <span className="block text-[10px] text-ink-mid">
                          {EMPLOYMENT_LABEL[member.employmentType]} · {member.weekHours} h this week{member.via === 'offer' ? ` · offered in turn at ${time(member.toldAt)}` : ''}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5">
                        <span className={`mr-1 inline-block h-2 w-2 rounded-full align-middle ${TIER_DOT[member.tier]}`} aria-hidden="true" />
                        {TIER_LABELS[member.tier]} · {member.score}
                      </td>
                      <td className="px-2 py-1.5">
                        <ReachedBy member={member} />
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-ink-mid">{member.reach?.readAt ? time(member.reach.readAt) : member.reach?.read ? 'Seen' : 'Not yet'}</td>
                      <td className={`px-2 py-1.5 ${answer.tone}`}>{answer.text}</td>
                      <td className="px-2 py-1.5 text-right">
                        {assignable && member.answer !== 'declined' && (
                          <button
                            type="button"
                            disabled={board.assign.isPending}
                            aria-busy={assigning(member.workerId)}
                            className="inline-flex min-h-8 items-center gap-1 rounded border border-coral bg-white px-2 py-1 text-[11px] font-bold text-coral hover:bg-coral-lt disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={() => board.assign.mutate({ postingId, workerId: member.workerId, reasonCode: 'ASSIGNED_FROM_TODAY' })}
                          >
                            <UserRound className="h-3 w-3" aria-hidden="true" />
                            {assigning(member.workerId) ? 'Assigning…' : 'Assign'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="px-3 py-2 text-[11px] text-ink-mid">{audience.blastAt ? 'Everyone was told at once before the list was kept (26 October 2026), so who it was is not on record.' : 'Nobody has been told yet.'}</p>
      )}
      <FormError error={board.assign.error} fallback="That did not go through. Try again." />
      {audience.notTold.length > 0 && (
        <div className="border-t border-marine/10 px-3 py-2">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">Not told ({audience.notTold.reduce((sum, group) => sum + group.workers.length, 0)}), and why</div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-ink-mid">
            {audience.notTold.map((group, index) => (
              <span key={group.reason}>
                {index > 0 && ' · '}
                <strong className="text-ink">{NOT_TOLD_LABEL[group.reason]}:</strong> {group.workers.map((worker) => worker.name).join(', ')}
              </span>
            ))}
          </p>
        </div>
      )}
      {callout && (
        <div className="border-t border-marine/10 px-3 py-2 text-[11px] font-bold text-marine-dk">
          {calloutLine(callout, (workerId) => audience.told.find((member) => member.workerId === workerId)?.name ?? callout.pool.find((member) => member.workerId === workerId)?.name ?? workerId)}
        </div>
      )}
      <PostingActions facilityId={facilityId} period={period} audience={audience} canAssign={assignable} canOpenCallout={canOpenCallout} vacatedFrom={vacatedFrom} callout={callout} />
      <div className="px-3 pb-2">
        <EndpointHint>GET /facilities/{'{facilityId}'}/schedule/board/postings/{'{postingId}'}/audience</EndpointHint>
      </div>
    </div>
  )
}
